<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { AdvancedMarker } from 'vue3-google-map';

import GlobalGoogleMap from '@/components/maps_canvases/GlobalGoogleMap.vue';
import { buildMarker } from '@/helpers/googleMapsHelper.ts';
import type { LocaleSchema } from '@/locales/index.ts';
import type { Region } from '@/typings/regions/Region.ts';
  
const  { t } = useI18n<{ message: LocaleSchema }>({
  useScope: 'global',
});

defineProps<{
  regions: Region[];
}>();

const model = defineModel<Region>();
const hoveredModel = defineModel<Region>('hoveredModel');

const emit = defineEmits<{
  'click-marker': [region: Region];
  'map-loaded': [void];
}>();

const markerElements = ref<HTMLDivElement[]>([]);

function getMarkerOptions(region: Region): google.maps.marker.AdvancedMarkerElementOptions {
  const descriptions = [
    {
      title: t('browsingApp.regions.projectCount'),
      data: region.projectCount
    }
  ];

  // Ajouter l'indicateur de promotion si la ville a des promotions
  if (region.hasPromotion) {
    descriptions.push({
      title: t('browsingApp.cards.promotion'),
      data: 1
    });
  }

  const marker = buildMarker({
    itemId: region.id,
    infoTitle: region.localizedName,
    descriptions,
    mouseenterHandler: (event) => {
      onMouseEnterMarker(event, region);
    },
    mouseleaveHandler: onMouseLeaveMarker
  });

  if(!markerElements.value.some(el => el.dataset.itemId === region.id.toString())) {
    markerElements.value.push(marker);
  }

  return {
    position: { lat: region.latitude, lng: region.longitude },
    title: region.name,
    content: marker,
    gmpClickable: true,
  };
}

// Gestionnaires d'événements
function onClickMarker({ domEvent }: { domEvent: PointerEvent }, item: Region) {
  const { target } = domEvent;
  const marker = (target as SVGElement).closest('gmp-advanced-marker') as HTMLButtonElement;
  marker.setAttribute('aria-expanded', 'true');
  model.value = item;
  emit('click-marker', item);
}

function onMouseEnterMarker(event: MouseEvent, region: Region) {
  const { target } = event;
  setHoveredState(target as HTMLDivElement, true);
  hoveredModel.value = region;
}

function onMouseLeaveMarker(event: MouseEvent) {
  hoveredModel.value = undefined;
}

function setHoveredState(marker: HTMLDivElement, hovered: boolean) {
  const advancedMarkerElement = marker.closest('gmp-advanced-marker') as HTMLButtonElement;
  advancedMarkerElement.dataset.hoveredItem = hovered ? 'true' : 'false';
}
</script>

<template>
  <GlobalGoogleMap :initial-items="regions"
                   class="globalGoogleMap_regions"
                   @map-loaded="emit('map-loaded')">
    <template #markers>
      <AdvancedMarker v-for="region in regions"
                      :key="region.id"
                      :options="getMarkerOptions(region)"
                      @click="onClickMarker($event, region)">
      </AdvancedMarker>
    </template>
  </GlobalGoogleMap>
</template>

<style lang="scss">

</style>
